import { Fundraiser } from '~/types/campaign';
import { apiUrl, authHeaders } from './auth';
import {
    FundRequest,
    FundRequestStatus,
    Vendor,
    VendorSubmit,
} from '~/types/fundrequests';
import queryString from 'query-string';
import imageCompression from 'browser-image-compression';

export const fetchAnnualRates = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/fundrequests/annualRates';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequestVendors = async ({
    page,
    pageSize,
    campaignId,
    sort_by = "created_date",
    sort = "desc"
}: {
    page?: number;
    pageSize?: number;
    campaignId?: number;
    sort_by?: string;
    sort: "asc" | "desc";
}) => {
    const headers = authHeaders();
    const filterParams = queryString.stringify(
        { page, pageSize, campaign_id: campaignId, sort_by, sort },
        {
            skipNull: true,
            skipEmptyString: true,
        }
    );

    const url = apiUrl + '/v2/fundrequests/vendors?' + filterParams;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchVendorOptions = async (campaignId: number) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/fundrequests/vendorOptions';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchVendorDetail = async (
    vendorId: number,
    campaignId: number,
    options?: { includeFundRequests?: boolean; limitFundRequests?: number }
) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + `/v2/fundrequests/vendors/${vendorId}`;
    
    // Build query parameters
    const params = new URLSearchParams();
    if (campaignId) {
        params.append('campaign_id', campaignId.toString());
    }
    // By default, skip loading fund requests for better performance
    if (options?.includeFundRequests !== undefined) {
        params.append('include_fund_requests', options.includeFundRequests.toString());
    } else {
        // Default to false for faster loading
        params.append('include_fund_requests', 'false');
    }
    if (options?.limitFundRequests) {
        params.append('limit_fund_requests', options.limitFundRequests.toString());
    }
    
    const url = baseUrl + '?' + params.toString();
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const createVendor = async (
    data: any,
    campaign_id?: string
): Promise<Vendor> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/vendors';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
    });

    const json = await resp.json();
    return json;
};

export const createFundRequest = async (
    data: VendorSubmit,
    campaign_id?: string
): Promise<FundRequest> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequest';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data), // Send the data as JSON
    });

    const json = await resp.json();
    return json;
};

export const updateFundRequest = async (
    data: VendorSubmit,
    fundrequest_id: string,
    campaign_id?: string
): Promise<FundRequest> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/' + fundrequest_id;
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data), // Send the data as JSON
    });

    const json = await resp.json();
    return json;
};

export const achDocumentPresignedUrl = async (
    fundrequest_id: string,
    fileName: string
) => {
    const headers = authHeaders();
    const url =
        apiUrl +
        '/v2/fundrequests/' +
        fundrequest_id +
        '/ach/presignedUrl?filename=' +
        fileName;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const submitFundRequest = async (
    fundrequest_id: string,
    campaign_id?: string
): Promise<FundRequestStatus> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/' + fundrequest_id + '/status';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status: 'submitted' }), // Send the data as JSON
    });
    const json = await resp.json();
    return json;
};

export const deleteFundRequest = async (
    fundrequest_id: number,
    campaign_id?: string
): Promise<any> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/' + fundrequest_id;
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    console.log('resp: ', resp);
    return resp;
};

export const fetchFundRequest = async (
    id: string,
    campaign_id: string
): Promise<FundRequest> => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/fundrequests/' + id;
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequestCategories = async (): Promise<Vendor[]> => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/fundrequests/categories';
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequests = async (
    { page = 1, pageSize = 20, status = '', sort_by = '', sort_order = '' },
    campaign_id?: string
) => {
    const headers = authHeaders();
    const filterParams = queryString.stringify(
        { page, pageSize, status, sort_by, sort_order },
        {
            skipNull: true,
            skipEmptyString: true,
        }
    );
    const baseUrl = apiUrl + '/v2/fundrequests?' + filterParams;
    const url = campaign_id ? baseUrl + '&campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequestVendorDetails = async (
    vendorId: number,
    campaignId?: string,
    options?: { includeFundRequests?: boolean; limitFundRequests?: number }
) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + `/v2/fundrequests/vendors/${vendorId}`;
    
    // Build query parameters
    const params = new URLSearchParams();
    if (campaignId) {
        params.append('campaign_id', campaignId);
    }
    // This function is used by PayeeDetails which needs fund requests, but limit to 50
    if (options?.includeFundRequests !== undefined) {
        params.append('include_fund_requests', options.includeFundRequests.toString());
    } else {
        params.append('include_fund_requests', 'true');
    }
    if (options?.limitFundRequests !== undefined) {
        params.append('limit_fund_requests', options.limitFundRequests.toString());
    } else {
        // Default limit to 50 most recent fund requests for performance
        params.append('limit_fund_requests', '50');
    }
    
    const url = baseUrl + '?' + params.toString();
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequestVendorMIPDetails = async (
    vendorId: string, // Vendor ID in Django
    vendor_id: string, // Vendor ID in MIP
    campaignId?: string
) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + `/v2/fundrequests/vendors/${vendorId}/mip`;
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ vendor_id }),
    });
    const json = await resp.json();
    return json;
};

export const populateVendors = async (campaignId?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + `/v2/fundrequests/populateVendors`;
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchFundRequestItemPresignedUrl = async (
    uploadPath: string,
    fileName: string
) => {
    const headers = authHeaders();
    const url = apiUrl + `${uploadPath}/presignedUrl?filename=${fileName}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const uploadFile = async (
    fields: Record<string, string>,
    url: string,
    file: File
) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
        formData.append(k, v);
    });
    formData.append('file', file);
    const resp = await fetch(url, {
        method: 'POST',
        body: formData,
    });
    return resp;
};

// Function to resize images before upload
export const resizeImage = async (file: File) => {
    // If the file is not an image, return the file
    if (!file.type.startsWith('image/')) {
        return file;
    }
    const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,
        useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
}

export const saveFundRequestItemImage = async (
    uploadPath: string,
    fileName: string,
    image_url: string
) => {
    const headers = authHeaders();
    const url = apiUrl + `${uploadPath}/saveUrl?filename=${fileName}`;

    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ image_url }),
    });

    const json = await resp.json();
    return json;
};

export const deleteFundRequestItemImage = async (deletePath: string) => {
    const headers = authHeaders();
    const url = apiUrl + deletePath;
    console.log('url: ', url);

    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });

    return resp;
};

export const fetchFundRequestDashboardNotifications = async (
    campaignId?: string
) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/fundrequests/dashboardNotifications';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const submitForAppeal = async (
    fundrequest_id: string,
    data?: { plea: string },
    campaign_id?: string,
): Promise<FundRequestStatus> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/' + fundrequest_id + '/appeal';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data), // Send the data as JSON
    });
    const json = await resp.json();
    return json;
}

export const addClientFundRequestNote = async (
    fundrequest_id: string,
    note: string,
    campaign_id?: string
) => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/' + fundrequest_id + '/notes';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ note }),
    });
    const json = await resp.json();
    return json;
};

export const markClientFundRequestNoteRead = async (
    note_id: number,
    campaign_id?: string
) => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/notes/' + note_id + '/mark-read';
    const url = campaign_id ? baseUrl + '?campaign_id=' + campaign_id : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchVendorChangeRequestNotifications = async (
    campaignId?: string
) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/vendor-change-requests/notifications';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const markVendorChangeRequestNotificationsAsRead = async (
    notificationIds: number[],
    campaignId?: string
) => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/vendor-change-requests/notifications';
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ notification_ids: notificationIds }),
    });
    const json = await resp.json();
    return json;
};

export const deleteVendor = async (
    vendorId: number,
    campaignId?: string
): Promise<any> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const baseUrl = apiUrl + '/v2/fundrequests/vendors/' + vendorId;
    const url = campaignId ? baseUrl + '?campaign_id=' + campaignId : baseUrl;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    return resp;
};
